import { ChangeDetectionStrategy, Component, ViewEncapsulation, computed, input, output, signal } from '@angular/core';
import { OverlayModule, ConnectedPosition } from '@angular/cdk/overlay';

import type { AgentModelTier } from '../../agent.types';

// One coarse tier option rendered in the picker menu. Labels are intentionally model-name-free; the
// `subtext` carries the muted helper line so "Heavy" never reads like the obvious upgrade. `icon` is
// the leading glyph shown both on the trigger (for the active tier) and beside the menu row.
interface ModelTierOption {
  tier: AgentModelTier;
  label: string;
  subtext: string;
  icon: string;
}

// The trigger sits at the very bottom of the composer, so the menu must open UPWARD: the overlay's
// bottom edge is pinned to the trigger's top edge, with a small gap. The downward variants are kept
// only as last-resort fallbacks for when there is no room above.
const OVERLAY_POSITIONS: ConnectedPosition[] = [
  { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom', offsetY: -6 },
  { originX: 'end', originY: 'top', overlayX: 'end', overlayY: 'bottom', offsetY: -6 },
  { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetY: 6 },
  { originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top', offsetY: 6 },
];

/**
 * Self-contained model-tier picker for the Workbuddy Agent composer (Default / Heavy).
 *
 * Renders a quiet trigger button showing only the current tier's icon (never a model name) and, on
 * click, a small menu that floats above the trigger via a CDK connected overlay. Selecting a tier
 * emits `tierChange` and closes the menu. This component owns its own overlay + LESS so it no longer
 * depends on — or mutates — any shared UI primitive (the old `wb-button-dropdown` + content styles).
 */
@Component({
  selector: 'agent-model-tier-picker',
  standalone: true,
  imports: [OverlayModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  templateUrl: './model.tier.picker.component.html',
  styleUrls: ['./model.tier.picker.component.less'],
})
export class AgentModelTierPickerComponent {

  protected readonly OVERLAY_POSITIONS = OVERLAY_POSITIONS;

  // ─── Inputs ─────────────────────────────────────────────────────────────────

  // The composer's current tier. Drives the trigger icon and the menu's selected row.
  public readonly $tier = input.required<AgentModelTier>({ alias: 'tier' });
  // Whether the Heavy tier may be selected. When false, the Heavy row is hidden entirely (the
  // composer hides the whole picker via the feature flag, so this is a belt-and-braces gate).
  public readonly $heavyEnabled = input(true, { alias: 'heavyEnabled' });
  // Once a chat has started the tier is frozen for that thread: the menu still opens and lists both
  // tiers, but the non-selected tier is disabled with a hint nudging the user to start a new chat.
  public readonly $locked = input(false, { alias: 'locked' });
  public readonly $disabled = input(false, { alias: 'disabled' });

  // ─── Outputs ────────────────────────────────────────────────────────────────

  public tierChange = output<AgentModelTier>();

  // ─── Internal State ─────────────────────────────────────────────────────────

  protected readonly $open = signal(false);

  // The two coarse tiers. Static — the visible set is filtered by $heavyEnabled below.
  private readonly allOptions: ModelTierOption[] = [
    { tier: 'default', label: 'Default', subtext: 'For everyday tasks', icon: 'fa-regular fa-feather' },
    { tier: 'heavy', label: 'Heavy', subtext: 'Best for harder tasks', icon: 'fa-regular fa-gauge-high' },
  ];

  // ─── Computed ───────────────────────────────────────────────────────────────

  // Menu rows: Heavy is dropped when it cannot be selected.
  protected readonly $options = computed(() => this.allOptions.filter(option => option.tier !== 'heavy' || this.$heavyEnabled()));

  // The icon shown on the trigger — the current tier's glyph, or the default glyph as a fallback.
  protected readonly $triggerIcon = computed(() => this.allOptions.find(option => option.tier === this.$tier())?.icon ?? this.allOptions[0].icon);

  // The label shown on the trigger — the current tier's name (e.g. "Default"/"Heavy"). Tier names are
  // not model names, so surfacing the active one in the composer is fine and keeps the choice visible.
  protected readonly $triggerLabel = computed(() => this.allOptions.find(option => option.tier === this.$tier())?.label ?? this.allOptions[0].label);

  // ─── Event Handlers ─────────────────────────────────────────────────────────

  protected toggle(): void {

    if (this.$disabled()) return;
    this.$open.update(open => !open);
  }

  protected close(): void {

    this.$open.set(false);
  }

  protected select(option: ModelTierOption): void {

    // A locked chat freezes the tier: only the already-selected row is actionable (a no-op), the
    // other is disabled. Guard here so a stray click on the disabled row never emits a change.
    if (this.$locked() && option.tier !== this.$tier()) return;

    if (option.tier !== this.$tier()) {
      this.tierChange.emit(option.tier);
    }
    this.close();
  }

  // Whether a menu row is disabled (locked chat, non-selected tier).
  protected isRowDisabled(option: ModelTierOption): boolean {

    return this.$locked() && option.tier !== this.$tier();
  }
}
